import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { API_ROOT } from 'src/app/_core/models/api-route.model';
import { SessionService } from 'src/app/_core/session.service';
import { SettingsService } from 'src/app/_core/settings/settings.service';
import { StoreService } from 'src/app/_core/store.service';

export interface Translation {
  id: number;
  key: string;
  value: string;
  language: string;
}

export interface Language {
  id: number;
  code: string; // ISO 639-1 code
  name: string; // Native name of language
  description: string; // English name of language
  caption?: string; // Custom name, combination of code and name.
}

@Injectable({
  providedIn: 'root'
})
export class TranslationsService {
  static translateService: TranslateService;

  private httpClient = inject(HttpClient);
  private translateService = inject(TranslateService);
  private sessionService = inject(SessionService);
  private settingsService = inject(SettingsService);
  private storeService = inject(StoreService);

  /**
   * Returns the translated value or the key if the value was not found.
   */
  static get(key?: string): string {
    if (!key || !TranslationsService.translateService || !TranslationsService.translateService.instant) {
      return key;
    }
    return TranslationsService.translateAndReplacePlaceholder(key);
  }

  constructor() {
    const translateService = this.translateService;

    TranslationsService.translateService = translateService;
  }

  /**
   * Finds '%s' in return value and replaces with whatever comes after the ',' in the translation key.
   * E.g. GOT_X_MINUTES,10 -> Got %s minutes -> Got 10 minutes.
   */
  private static translateAndReplacePlaceholder(key: string): string {
    if (!key) {
      return;
    }
    const keySplit = key.split(',');
    const keyBase = keySplit[0];
    const params: string[] = keySplit.slice(1, keySplit.length);
    if (params.length === 0) {
      return TranslationsService.translateService.instant(keyBase);
    }
    let translation = TranslationsService.translateService.instant(keySplit[0]);
    if (translation === keyBase) {
      return key;
    }
    params.forEach((param) => {
      translation = translation.replace('%s', param);
    });
    return translation;
  }

  /**
   * Initialized at app startup from core.module.ts
   * Get translations and set ngx-translate
   */
  async initialize(): Promise<void> {
    const language = this.getLanguage();
    return new Promise<void>((resolve) => {
      forkJoin([this.setFallbackTranslations(), this.setTranslations(language)]).subscribe(
        () => {
          resolve();
        },
        () => {
          window.alert('AGR Error: Failed to load translations from API\nPotential App Service error!');
          console.error('AGR: Failed to load translations from API - Check App Service');
          resolve();
        }
      );
    });
  }

  getLanguage(): string {
    let lang = this.sessionService.user ? this.sessionService.user.language : undefined;
    lang = lang ? lang : (this.storeService.get('lastUsedLanguage') as string);
    lang = lang ? lang : this.settingsService.defaultLanguage();
    return lang ? lang : 'en';
  }

  /**
   * Activates language for app.
   */
  setTranslations(language: string): Observable<boolean> {
    return this.httpClient.get(`${API_ROOT}/translations/${language}`).pipe(
      map((translations: Translation[]) => {
        this.translateService.setTranslation(language, this.toTranslationsObj(translations));
        this.activateLanguage(language);
        return true;
      })
    );
  }

  /**
   * Save last used language.
   */
  storeLanguage(language: string): void {
    if (language === 'null') {
      return;
    }
    this.storeService.set('lastUsedLanguage', language);
  }

  getAvailableLanguages(): Observable<Language[]> {
    // Was served by pool.agrdynamics.com/api/languages, which no longer exists
    const languages: Language[] = [
      { id: 1, code: 'da', name: 'Dansk', description: 'Danish' },
      { id: 2, code: 'en-US', name: 'English, United States', description: 'English, United States' },
      { id: 3, code: 'en', name: 'English', description: 'English' },
      { id: 4, code: 'fo', name: 'Føroyskt', description: 'Faroese' },
      { id: 5, code: 'fr', name: 'Français', description: 'French' },
      { id: 6, code: 'is', name: 'Íslenska', description: 'Icelandic' },
      { id: 7, code: 'nb', name: 'Norsk Bokmål', description: 'Norwegian Bokmål' },
      { id: 8, code: 'sv', name: 'Svenska', description: 'Swedish' }
    ];
    return of(
      languages.map((language) => {
        language.caption = `${language.name} (${language.code})`;
        return language;
      })
    );
  }

  /**
   * Sets English as fallback language for app
   */
  private setFallbackTranslations(): Observable<boolean> {
    if (localStorage.getItem('disableFallbackTranslations') === 'true') {
      return of(true).pipe(finalize(() => true));
    }
    return this.httpClient.get(`${API_ROOT}/translations/en`).pipe(
      map((translations: Translation[]) => {
        this.translateService.setTranslation('en', this.toTranslationsObj(translations));
        this.translateService.setFallbackLang('en'); // setDefaultLang in ngx-translate 15
        return true;
      })
    );
  }

  /**
   * Activates language using ngx-translate and saves lastUsedLanguage in local storage.
   */
  private activateLanguage(language: string): void {
    this.translateService.use(language);
    this.storeLanguage(language);
  }

  private toTranslationsObj(translations: Translation[]): { [key in string]: string } {
    if (!translations || translations.length === 0) {
      return {};
    }
    return translations.reduce((acc, translation) => {
      acc[translation.key] = translation.value;
      return acc;
    }, {});
  }
}
